/**
 * PHASE 1 — Admin-only read-only "Suspected Splits" review panel.
 *
 * Surfaces FishTalk datasets whose fish number appears to have dropped
 * ~50% mid-cycle while the dataset continued to collect data — the
 * classic signature of a double-stocked cage being split into two
 * independent populations. Purely informational: no data is mutated,
 * no other view changes. Admin will confirm/reject each candidate in
 * a future Phase 2 patch that adds the actual 2 → 4 lineage split.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Loader2, Split } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuspectedSplitsPanel() {
  const { token, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/suspected-splits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.candidates || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load suspected splits');
      setRows([]);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!user?.is_admin) return null;

  return (
    <Card className="border-slate-200" data-testid="suspected-splits-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-[#0891b2] flex items-center gap-2">
          <Split className="w-4 h-4" /> Suspected Double-Stock Splits
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Read-only detection (Phase 1). Datasets whose fish number
          dropped 45–55 % mid-cycle while data collection continued.
          No cage has been split yet — this panel lists candidates
          for admin review before a future Phase 2 patch performs the
          2 → 4 lineage split.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3" data-testid="ssp-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning FishTalk datasets…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 py-3 flex items-center gap-2" data-testid="ssp-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-3" data-testid="ssp-empty">
            No suspected splits detected across your active FishTalk datasets.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ssp-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <th className="text-left py-2 px-2 font-semibold">Dataset</th>
                  <th className="text-left py-2 px-2 font-semibold">Unit</th>
                  <th className="text-left py-2 px-2 font-semibold">Split date</th>
                  <th className="text-right py-2 px-2 font-semibold">Initial fish</th>
                  <th className="text-right py-2 px-2 font-semibold">Drop</th>
                  <th className="text-right py-2 px-2 font-semibold">Remaining</th>
                  <th className="text-right py-2 px-2 font-semibold">Post-split days</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.record_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ssp-row-${r.record_id}`}>
                    <td className="py-2 px-2 font-medium text-slate-800">{r.dataset}</td>
                    <td className="py-2 px-2 text-slate-600">{r.current_unit || '—'}</td>
                    <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{r.split_date}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.initial_fish_number?.toLocaleString?.() ?? r.initial_fish_number}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                        {r.drop_percent}%
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.remaining_fish?.toLocaleString?.() ?? r.remaining_fish}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-500">{r.post_split_days_observed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 italic mt-3">
              Phase 1 — detection only. Reviewing this list will not modify any dataset or KPI.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
